import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  IconBookmark,
  IconFolder,
  IconPlayerPause,
  IconPlayerPlay,
  IconTrash,
} from '@tabler/icons-react';
import { confirmDialog } from '../../components/AppDialog';
import Markdown from '../../components/Markdown';
import { REC_LANGUAGE_OPTIONS, useRecording } from '../../contexts/RecordingContext';
import { MEETING_THEME, fmtDur, mdHardBreaks, softWrap } from '../../utils/meetingUtil';

const meterBars = [0.22, 0.52, 0.82, 0.38, 0.96, 0.64, 0.3, 0.74, 0.48, 0.9, 0.58, 0.28, 0.7, 0.44, 0.84, 0.36];
const T = MEETING_THEME;

export default function RecordingView({
  mobile,
  sel,
  onMinimize,
}) {
  const {
    recTitle,
    setRecTitle,
    recordingSec,
    recPaused,
    recPauseSupported,
    recLevel,
    recFinalizing,
    recPreview,
    recordingNotes,
    addRecordingNote,
    removeRecordingNote,
    pauseRec,
    resumeRec,
    stopRec,
    uploadRecPreview,
    restartRec,
    cancelRec,
    recordingFolderName,
    silenceWarnSec,
    keepRecording,
    recLanguage,
    setRecLanguage,
    recStarting,
    recResumable,
    continueRecoveredRec,
  } = useRecording();
  const [memoText, setMemoText] = useState('');
  const [langOpen, setLangOpen] = useState(false);
  const [langMenuMaxH, setLangMenuMaxH] = useState(null);
  const langWrapRef = useRef(null);
  const langBtnRef = useRef(null);
  const sectionRef = useRef(null);
  // 북마크 알림 — idx 는 "방금 남긴 노트"를 가리키는 임시값이고, 화면에 보이는 글은 text 하나다.
  // (idx 로 recordingNotes 를 매 렌더 다시 읽으면 2초 안에 메모를 지웠을 때 엉뚱한 항목의 시각이 보인다)
  const [bookmarkToastIdx, setBookmarkToastIdx] = useState(null);
  const [bookmarkToastText, setBookmarkToastText] = useState('');
  const bookmarkToastTimerRef = useRef(null);
  const stopConfirmRef = useRef(false);   // 확인 창이 떠 있는 동안 [종료] 두 번째 누름은 무시한다
  const noteCount = (recordingNotes || []).length;
  const noteLimitReached = noteCount >= 200;
  const previewMode = !!recPreview;
  const level = Math.max(0, Math.min(1, recLevel || 0));
  const elapsed = fmtDur(recordingSec) || '0:00';
  const statusText = previewMode ? '업로드 전 미리듣기' : (recFinalizing ? '녹음 처리 중' : (recPaused ? '일시정지' : '녹음 중'));
  // 🔴 RecordingContext 를 `{ useRecording }` 만으로 목한 시험들에서는 목록이 undefined 다.
  const langOptions = REC_LANGUAGE_OPTIONS || [];
  const langLabel = langOptions.find((l) => l.code === recLanguage)?.label || '';
  // 미리보기 단추 잠금 — 마무리 중이거나 [이어 녹음]을 시작하는 동안(그 미리보기에 새 구간을 붙이는 중).
  // 🔴 recStarting 이 없는 목(시험)에서는 undefined → 종전처럼 recFinalizing 만 본다.
  const previewLocked = !!(recFinalizing || recStarting);
  // 이어 녹음 구간 수(2개 이상이면 업로드 때 서버가 이어붙인다). 재생은 첫 구간 하나만 한다.
  const previewSegmentCount = Array.isArray(recPreview?.segments) ? recPreview.segments.length : 0;

  const saveMemo = () => {
    const text = memoText.trim();
    if (!text || noteLimitReached || recFinalizing || previewMode) return;
    const idx = addRecordingNote(text);
    if (idx != null) setMemoText('');
  };

  const addBookmark = () => {
    if (noteLimitReached || recFinalizing || previewMode) return;
    const idx = addRecordingNote('');
    if (idx == null) return;   // 녹음 중이 아니거나 메모 200개 — 남은 게 없으니 알림도 없다
    setBookmarkToastIdx(idx);
    if (bookmarkToastTimerRef.current) clearTimeout(bookmarkToastTimerRef.current);
    bookmarkToastTimerRef.current = setTimeout(() => {
      bookmarkToastTimerRef.current = null;
      setBookmarkToastText('');
    }, 2000);
  };

  // 단축키 Alt+Shift+B — 핸들러가 최신 addBookmark(가드 포함)를 보도록 ref 로 건넨다.
  const addBookmarkRef = useRef(addBookmark);
  addBookmarkRef.current = addBookmark;

  useEffect(() => {
    const onKeyDown = (e) => {
      if (!e.altKey || !e.shiftKey || e.ctrlKey || e.metaKey) return;
      // 🔴 e.key 는 Alt 조합에서 자판·OS 에 따라 다른 글자가 되므로 물리 키(e.code)로 본다.
      // 누름 유지(repeat)·IME 조합 중은 흘려보내고, 화면이 둘 떠 있어도(분할 보기) 먼저 받은
      // 쪽이 preventDefault 하므로 defaultPrevented 로 한 번 누름에 북마크 하나만 남긴다.
      if (e.code !== 'KeyB' || e.repeat || e.isComposing || e.defaultPrevented) return;
      e.preventDefault();
      addBookmarkRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => () => {
    if (bookmarkToastTimerRef.current) clearTimeout(bookmarkToastTimerRef.current);
  }, []);

  // «언어» 메뉴 — 마무리 중·미리듣기로 넘어가면 닫는다(그때는 setRecLanguage 가 false 다).
  useEffect(() => {
    if (recFinalizing || previewMode) setLangOpen(false);
  }, [recFinalizing, previewMode]);

  // 메뉴 최대 높이 — section 이 overflow:hidden 이라 낮은 화면에서 메뉴 위쪽이 잘린다.
  // 열 때 한 번 «언어» 단추 위에 남은 높이를 재서 그 안으로 묶는다(아래 한계 96 · 못 재면 걸지 않음).
  useLayoutEffect(() => {
    if (!langOpen) { setLangMenuMaxH(null); return; }
    const btn = langBtnRef.current, sec = sectionRef.current;
    if (!btn || !sec) { setLangMenuMaxH(null); return; }
    const avail = btn.getBoundingClientRect().top - sec.getBoundingClientRect().top - 16;   // 단추 위 간격 8 + 위쪽 여백 8
    setLangMenuMaxH(avail > 0 ? Math.max(96, Math.floor(avail)) : null);
  }, [langOpen]);

  // 열려 있을 때만 바깥 누름·Escape 를 듣는다.
  useEffect(() => {
    if (!langOpen) return undefined;
    const onMouseDown = (e) => {
      if (langWrapRef.current && !langWrapRef.current.contains(e.target)) setLangOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      setLangOpen(false);
      langBtnRef.current?.focus();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [langOpen]);

  const pickLanguage = (code) => {
    setRecLanguage?.(code);
    setLangOpen(false);
    langBtnRef.current?.focus();
  };

  // [종료] = 확인 창을 거쳐 바로 업로드·받아쓰기(미리듣기를 거치지 않는다).
  // 확인 창이 떠 있는 동안에도 녹음은 계속되고, 그 사이 무음 자동중지 등으로 이미 끝났으면
  // stopRec 쪽 가드(세션 없음·마무리 중)가 아무 일도 하지 않는다.
  const requestStop = async () => {
    if (recFinalizing || previewMode || stopConfirmRef.current) return;
    stopConfirmRef.current = true;
    try {
      const ok = await confirmDialog('녹음이 종료되면 음성을 텍스트로 변환합니다.', {
        title: '녹음을 종료할까요?',
        okText: '녹음 종료',
        cancelText: '취소',
      });
      if (ok) stopRec({ autoUpload: true });
    } finally {
      stopConfirmRef.current = false;
    }
  };

  // 알림 글은 북마크 직후 "그 항목의 시각"으로 한 번만 굳힌다(아래 메모 목록 칩과 같은 식).
  // 🔴 elapsed 는 1초 tick 의 floor 라 일시정지 뒤 저장 시각보다 1초 이를 수 있어 쓰지 않는다.
  // 🔴 useEffect 로 미루면 idx 만 바뀐 커밋이 한 번 끼므로 렌더 중에 조정한다.
  if (bookmarkToastIdx != null) {
    const saved = (recordingNotes || [])[bookmarkToastIdx];
    setBookmarkToastText(saved ? `${fmtDur(Math.floor(saved.time || 0)) || '0:00'} 북마크 되었어요.` : '');
    setBookmarkToastIdx(null);
  }

  // 모바일 아래 여백 = 탭바 몫(60) + 두 줄 바·안내문까지 올라간 녹음 바 상자보다 넉넉히(마지막 메모가 바에 안 가리게).
  const pagePadding = mobile ? '22px 16px 240px' : '38px 56px 176px';
  const pillWidth = previewMode
    ? (mobile ? 'calc(100% - 24px)' : 'min(760px, calc(100% - 80px))')
    : (mobile ? 'calc(100% - 24px)' : 'min(480px, calc(100% - 80px))');

  return (
    <section ref={sectionRef} style={{ flex: 1, minWidth: 0, position: 'relative', overflow: 'hidden', background: '#fbfbfa' }}>
      <div style={{ height: '100%', overflowY: 'auto', padding: pagePadding, boxSizing: 'border-box' }}>
        <div style={{ maxWidth: '980px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
            <input
              value={recTitle}
              onChange={(e) => setRecTitle(e.target.value)}
              placeholder='새 노트'
              maxLength={120}
              style={{
                flex: 1,
                minWidth: 0,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                color: '#32312e',
                fontSize: mobile ? '30px' : '42px',
                lineHeight: 1.15,
                fontWeight: 800,
                letterSpacing: 0,
                padding: 0,
              }}
            />
            <button
              type="button"
              onClick={onMinimize}
              style={{
                background: '#f1f1ef',
                color: '#32312e',
                border: '1px solid #e5e5e2',
                borderRadius: '8px',
                padding: '7px 12px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              ← 목록
            </button>
            <button
              type="button"
              onClick={cancelRec}
              disabled={recFinalizing}
              title='녹음 취소'
              style={{
                width: 40,
                height: 40,
                border: '1px solid #e5e5e2',
                borderRadius: '8px',
                background: '#fff',
                color: '#64635f',
                cursor: recFinalizing ? 'not-allowed' : 'pointer',
                fontSize: '18px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                opacity: recFinalizing ? 0.55 : 1,
              }}
            >
              <IconTrash size={18} />
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', border: '1px solid #e5e5e2', background: '#fff', color: '#2383e2', borderRadius: '999px', padding: '6px 12px', fontSize: '13px', fontWeight: 700, maxWidth: '100%', ...softWrap }}>
              <IconFolder size={15} />{recordingFolderName || sel?.name || '미분류'}
            </span>
            <span style={{ color: previewMode || recFinalizing || recPaused ? '#64635f' : '#059669', fontSize: '13px', fontWeight: 800 }}>
              {statusText} · {elapsed}
            </span>
          </div>

          {silenceWarnSec != null && !previewMode && (
            <div style={{
              marginTop: mobile ? '24px' : '32px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'wrap',
              border: '1px solid #fcd34d',
              background: '#fffbeb',
              borderRadius: '10px',
              padding: mobile ? '12px 14px' : '14px 18px',
            }}>
              <span style={{ color: '#92400e', fontSize: '14px', fontWeight: 800 }}>
                소리가 들리지 않습니다 · {silenceWarnSec}초 후 자동으로 중지합니다
              </span>
              <button
                type="button"
                onClick={keepRecording}
                style={{
                  marginLeft: 'auto',
                  border: '1px solid #d97706',
                  background: '#fff',
                  color: '#b45309',
                  borderRadius: '8px',
                  padding: '8px 14px',
                  fontSize: '13px',
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                계속 녹음
              </button>
            </div>
          )}

          <div style={{ marginTop: mobile ? '34px' : '54px' }}>
            <div style={{ color: '#64635f', fontSize: '13px', fontWeight: 800, marginBottom: '12px', fontVariantNumeric: 'tabular-nums' }}>
              {previewMode ? '녹음 시간' : '현재 녹음시간'} {elapsed}
            </div>
            <textarea
              value={memoText}
              onChange={(e) => setMemoText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  saveMemo();
                }
              }}
              disabled={recFinalizing || previewMode || noteLimitReached}
              rows={2}
              maxLength={1000}
              placeholder='메모를 작성해 보세요 · 마크다운 지원 · Enter로 현재 시점에 저장, Shift+Enter 줄바꿈'
              style={{
                width: '100%',
                boxSizing: 'border-box',
                resize: 'vertical',
                border: '1px solid #e5e5e2',
                borderRadius: '8px',
                background: '#fff',
                padding: mobile ? '16px' : '22px',
                fontFamily: 'inherit',
                fontSize: mobile ? '16px' : '18px',
                lineHeight: 1.7,
                color: '#32312e',
                outlineColor: T.color.green,
                minHeight: mobile ? '84px' : '104px',
                boxShadow: '0 1px 2px rgba(15,23,42,.04)',
              }}
            />
            <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'space-between', gap: '10px', color: '#92918d', fontSize: '12px', fontWeight: 700 }}>
              <span>메모 {noteCount}/200</span>
              <span>{memoText.length}/1000</span>
            </div>
          </div>

          <div style={{ marginTop: mobile ? '24px' : '34px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {(recordingNotes || []).map((n, i) => {
              const noteTime = fmtDur(Math.floor(n.time || 0)) || '0:00';
              return (
                <div key={`${n.time}-${i}`} style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr 32px' : '88px 1fr 32px', gap: '10px', alignItems: 'start', padding: mobile ? '12px 0' : '14px 0', borderBottom: '1px solid #e5e5e2' }}>
                  <span style={{ color: T.color.selectedText, fontSize: '13px', fontWeight: 800, fontVariantNumeric: 'tabular-nums', gridColumn: mobile ? '1 / 2' : 'auto' }}>
                    {noteTime}
                  </span>
                  <div style={{ minWidth: 0, color: n.text ? '#32312e' : '#047857', fontSize: '15px', lineHeight: 1.65, gridColumn: mobile ? '1 / 3' : 'auto', gridRow: mobile ? '2 / 3' : 'auto', ...softWrap }}>
                    {n.text ? <Markdown compact>{mdHardBreaks(n.text)}</Markdown> : <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}><IconBookmark size={14} />{noteTime}</span>}
                  </div>
                  <button
                    onClick={() => removeRecordingNote(i)}
                    disabled={recFinalizing || previewMode}
                    title='메모 삭제'
                    style={{ border: 'none', background: 'transparent', color: '#92918d', cursor: recFinalizing || previewMode ? 'not-allowed' : 'pointer', fontSize: '18px', lineHeight: 1, padding: '2px 6px', opacity: recFinalizing || previewMode ? 0.45 : 1, gridColumn: mobile ? '2 / 3' : 'auto', gridRow: mobile ? '1 / 2' : 'auto' }}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{ position: 'absolute', left: '50%', bottom: mobile ? 12 : 22, transform: 'translateX(-50%)', width: pillWidth, zIndex: 30 }}>
        {/* 북마크 알림 — 영역은 늘 두고(aria-live 가 살아 있게) 글이 있을 때만 보인다 */}
        <div
          data-testid="rec-bookmark-toast"
          role="status"
          aria-live="polite"
          style={{ display: 'flex', justifyContent: 'center', marginBottom: bookmarkToastText ? '8px' : 0, pointerEvents: 'none' }}
        >
          {bookmarkToastText ? (
            <span style={{ borderRadius: '999px', background: 'rgba(17,24,39,.92)', color: '#d1fae5', padding: '6px 14px', fontSize: '12px', fontWeight: 800, whiteSpace: 'nowrap', boxShadow: '0 8px 20px rgba(15,23,42,.24)' }}>
              {bookmarkToastText}
            </span>
          ) : null}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: previewMode ? (mobile ? '8px' : '12px') : (mobile ? '5px' : '6px'), flexWrap: mobile ? 'wrap' : 'nowrap', borderRadius: '999px', background: '#111827', color: '#fff', padding: previewMode ? (mobile ? '10px 12px' : '12px 16px') : (mobile ? '5px 8px' : '6px 10px'), boxShadow: previewMode ? '0 18px 42px rgba(15,23,42,.28)' : '0 10px 24px rgba(15,23,42,.28)' }}>
          {previewMode ? (
            <>
              <audio
                controls
                src={recPreview.url}
                style={{ height: 36, minWidth: mobile ? 220 : 320, maxWidth: '100%', flex: mobile ? '1 1 100%' : '1 1 360px' }}
              />
              {previewSegmentCount >= 2 && (
                <span data-testid="rec-preview-segments" style={{ color: '#d1fae5', fontSize: '12px', fontWeight: 800, whiteSpace: 'nowrap' }}>
                  구간 {previewSegmentCount}개 — 업로드하면 이어붙입니다
                </span>
              )}
              <button onClick={uploadRecPreview} disabled={previewLocked} style={{ minHeight: 36, borderRadius: '999px', border: 'none', background: '#10b981', color: '#052e1f', cursor: previewLocked ? 'not-allowed' : 'pointer', padding: '0 16px', fontSize: '13px', fontWeight: 900, whiteSpace: 'nowrap', opacity: previewLocked ? 0.65 : 1 }}>업로드</button>
              {recResumable && (
                <button onClick={continueRecoveredRec} disabled={previewLocked} style={{ minHeight: 36, borderRadius: '999px', border: '1px solid #059669', background: '#064e3b', color: '#d1fae5', cursor: previewLocked ? 'not-allowed' : 'pointer', padding: '0 14px', fontSize: '13px', fontWeight: 800, whiteSpace: 'nowrap', opacity: previewLocked ? 0.5 : 1 }}>이어 녹음</button>
              )}
              <button onClick={restartRec} disabled={previewLocked} style={{ minHeight: 36, borderRadius: '999px', border: '1px solid #374151', background: '#1f2937', color: '#f8fafc', cursor: previewLocked ? 'not-allowed' : 'pointer', padding: '0 14px', fontSize: '13px', fontWeight: 800, whiteSpace: 'nowrap', opacity: previewLocked ? 0.5 : 1 }}>다시 녹음</button>
              <button onClick={cancelRec} disabled={previewLocked} style={{ minHeight: 36, borderRadius: '999px', border: '1px solid #374151', background: 'transparent', color: '#e5e7eb', cursor: previewLocked ? 'not-allowed' : 'pointer', padding: '0 14px', fontSize: '13px', fontWeight: 800, whiteSpace: 'nowrap', opacity: previewLocked ? 0.5 : 1 }}>취소</button>
            </>
          ) : (
            <>
              {recPauseSupported && (
                <button
                  onClick={recPaused ? resumeRec : pauseRec}
                  disabled={recFinalizing}
                  title={recPaused ? '녹음 재개' : '녹음 일시정지'}
                  style={{ width: 26, height: 26, borderRadius: '999px', border: 'none', background: '#1f2937', color: '#fff', cursor: recFinalizing ? 'not-allowed' : 'pointer', fontSize: '18px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: recFinalizing ? 0.5 : 1 }}
                >
                  {recPaused ? <IconPlayerPlay size={14} /> : <IconPlayerPause size={14} />}
                </button>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: mobile ? '110px' : '140px', flex: mobile ? '1 1 130px' : '0 1 180px' }}>
                <div style={{ height: 18, flex: '1 1 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px', minWidth: 0 }}>
                  {meterBars.map((shape, i) => (
                    <span key={i} style={{
                      width: 3,
                      height: `${5 + Math.round(shape * 11)}px`,
                      borderRadius: 999,
                      background: recPaused ? '#4b5563' : (level > i / (meterBars.length + 2) ? '#34d399' : '#475569'),
                      transform: `scaleY(${recPaused ? 0.18 : Math.max(0.18, Math.min(1, level * (0.72 + shape)))})`,
                      transformOrigin: 'center',
                      transition: 'transform .08s linear, background .12s linear',
                    }} />
                  ))}
                </div>
                <span style={{ color: '#f8fafc', fontSize: '11px', fontWeight: 800, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  녹음 중 {elapsed}
                </span>
              </div>

              <button
                onClick={addBookmark}
                disabled={recFinalizing || noteLimitReached}
                title='북마크 (Alt+Shift+B)'
                aria-label='북마크'
                style={{ minHeight: 24, borderRadius: '999px', border: 'none', background: '#1f2937', color: noteLimitReached ? '#64748b' : '#d1fae5', cursor: recFinalizing || noteLimitReached ? 'not-allowed' : 'pointer', padding: '0 8px', fontSize: '11px', fontWeight: 800, whiteSpace: 'nowrap' }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}><IconBookmark size={12} />북마크</span>
              </button>
              {/* 인식 언어 — 녹음 중에만 바꾼다(미리듣기 바에는 없다). 메뉴는 바 위로 뜬다. */}
              <div ref={langWrapRef} style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                <button
                  ref={langBtnRef}
                  type="button"
                  onClick={() => setLangOpen((open) => !open)}
                  disabled={recFinalizing}
                  title='인식 언어'
                  aria-label={langLabel ? `인식 언어 ${langLabel}` : '인식 언어'}
                  aria-haspopup="menu"
                  aria-expanded={langOpen}
                  style={{ minHeight: 24, borderRadius: '999px', border: 'none', background: '#1f2937', color: '#d1fae5', cursor: recFinalizing ? 'not-allowed' : 'pointer', padding: '0 8px', fontSize: '11px', fontWeight: 800, whiteSpace: 'nowrap', opacity: recFinalizing ? 0.5 : 1 }}
                >
                  {langLabel ? `언어 · ${langLabel}` : '언어'}
                </button>
                {langOpen && (
                  <div
                    role="menu"
                    aria-label="인식 언어 선택"
                    style={{ position: 'absolute', bottom: 'calc(100% + 8px)', right: 0, zIndex: 40, minWidth: 160, background: '#fff', color: '#32312e', border: '1px solid #e5e5e2', borderRadius: '10px', padding: '6px', boxShadow: '0 12px 28px rgba(15,23,42,.24)', boxSizing: 'border-box', maxHeight: langMenuMaxH ?? undefined, overflowY: 'auto' }}
                  >
                    {langOptions.map((l) => {
                      const checked = l.code === recLanguage;
                      return (
                        <button
                          key={l.code}
                          type="button"
                          role="menuitemradio"
                          aria-checked={checked}
                          onClick={() => pickLanguage(l.code)}
                          style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', borderRadius: '6px', background: checked ? '#ecfdf5' : 'transparent', color: checked ? '#047857' : '#32312e', cursor: 'pointer', padding: '7px 10px', fontSize: '13px', fontWeight: checked ? 800 : 600, whiteSpace: 'nowrap' }}
                        >
                          {l.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <button onClick={cancelRec} disabled={recFinalizing} style={{ minHeight: 24, borderRadius: '999px', border: '1px solid #374151', background: 'transparent', color: '#e5e7eb', cursor: recFinalizing ? 'not-allowed' : 'pointer', padding: '0 9px', fontSize: '11px', fontWeight: 800, whiteSpace: 'nowrap', opacity: recFinalizing ? 0.5 : 1 }}>취소</button>
              <button onClick={requestStop} disabled={recFinalizing} style={{ minHeight: 24, borderRadius: '999px', border: 'none', background: '#10b981', color: '#052e1f', cursor: recFinalizing ? 'not-allowed' : 'pointer', padding: '0 10px', fontSize: '11px', fontWeight: 900, whiteSpace: 'nowrap', opacity: recFinalizing ? 0.65 : 1 }}>종료</button>
            </>
          )}
        </div>
        <div style={{ textAlign: 'center', marginTop: previewMode ? '9px' : '5px', color: '#64635f', fontSize: previewMode ? '12px' : '11px', fontWeight: 700 }}>
          {previewMode ? '업로드 전 미리듣기를 확인해 주십시오.' : '녹음 전 상대방의 동의를 구하는 에티켓을 지켜 주십시오.'}
        </div>
        {/* 모바일: 하단 탭바(position:fixed · zIndex 1000)가 이 상자 아래쪽을 덮는다 — 바가 두 줄(«언어» 단추)이
            되면 둘째 줄 «취소»·«종료»와 위 안내문이 탭바 밑에 깔렸다. 탭바 몫만큼 바닥에 빈 자리를 둬 바·안내문
            전체를 탭바 위로 올린다. 높이는 MobileTabBar 가 심는 'gw-tabbar-pad' 규칙(키보드가 열려 탭바가
            숨으면 0)을 그대로 쓰고, 빈 자리는 누름을 아래 화면으로 흘려보낸다. 데스크톱엔 없다. */}
        {mobile && <div className="gw-tabbar-pad" data-testid="rec-tabbar-spacer" aria-hidden="true" style={{ pointerEvents: 'none' }} />}
      </div>
    </section>
  );
}
